import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
/*
 * ONE REGISTRY PER TAB, keyed by name.
 *
 * Module-level rather than an instance handed around: a tool belongs to the SURFACE, not to a
 * conversation or a manager, and threading a registry through every component that might want to
 * contribute one is the friction that stops anyone contributing. Registration is idempotent and
 * last-wins, so a hot module reload re-registering does not accumulate duplicates.
 */
const registry = new Map();
/** Bumped on every change, so a caller can cheaply tell whether the catalog it announced is still
 *  current without diffing schemas. */
let revision = 0;
/** Told when the set changes, so the plane's catalog can catch up without polling. */
const listeners = new Set();
/**
 * Declare capabilities this browser can run.
 *
 * Call it whenever, at boot, when a view mounts, when a feature flag flips. There is no
 * registration window and no deploy to wait for: the next turn sends whatever is registered when it
 * starts. Returns an unregister function, so a view that owns a tool can take it away when it
 * leaves rather than offering the model something that will fail.
 */
export function registerClientTools(...tools) {
    for (const t of tools)
        registry.set(t.name, t);
    changed();
    return () => {
        for (const t of tools) {
            // Only if it is still OURS. A later registration under the same name owns it now, and
            // removing it would be one view unregistering another's tool.
            if (registry.get(t.name) === t)
                registry.delete(t.name);
        }
        changed();
    };
}
function changed() {
    revision++;
    for (const fn of listeners) {
        try {
            fn();
        }
        catch {
            // A listener that throws must not stop the others, and must not fail the registration,
            // announcing the catalog is bookkeeping, and a tool works whether or not it was announced.
        }
    }
}
/**
 * Be told when the declared set changes.
 *
 * So the plane's catalog can catch up on a tool registered mid-session rather than only on the ones
 * present at handshake, otherwise "a tool works the day it is written" would quietly mean "…if you
 * reload first". Returns an unsubscribe.
 */
export function onClientToolsChanged(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}
/** Every client tool currently declared. */
export function clientTools() {
    return [...registry.values()];
}
/** Bumped whenever the set changes: an announce can skip when nothing moved. */
export function clientToolsRevision() {
    return revision;
}
/** The declarations to send with a request. */
export function clientToolDeclarations() {
    // Currently-unavailable tools are left out rather than offered-and-refused. The refusal still
    // exists in `dispatchClientTool` — this is what stops the model spending a turn discovering it.
    return clientTools()
        .filter((t) => !t.unavailable?.())
        .map(toDeclaration);
}
export function toDeclaration(t) {
    return { type: 'function', function: { name: t.name, description: t.description, parameters: t.parameters } };
}
/**
 * The same declarations, plus what the CATALOG needs and the model must not see.
 *
 * Kept separate from `toDeclaration` deliberately. That one is sent to a provider,
 * and providers validate the function object — an unknown field there risks a
 * rejected request on the path that actually matters. The announcement goes only
 * to our own worker, which is where the permission is wanted: it is what lets a
 * role be resolved into a tool list without the worker having to know in advance
 * what any UI ships.
 */
export function clientToolAnnouncements() {
    return clientTools().map((t) => ({
        type: 'function',
        function: { name: t.name, description: t.description, parameters: t.parameters, permission: t.permission },
    }));
}
/**
 * Run a client tool, or say it is not ours.
 *
 * `null`, not `{ok:false}`, when the name is unknown here, because "this is a server tool" and
 * "this tool failed" are different answers and the caller routes on the difference. Returning a
 * failure would make every server tool call look like a broken client tool.
 */
/** Is this name one of ours? Asked BEFORE dispatch, by the audit path, which has to open a record
 *  for a client tool and must not open one for a server tool that `RunTool` will record itself. */
export const isClientTool = (name) => registry.has(name);
export async function dispatchClientTool(name, argsJson, ctx = { conversationId: '', runId: '', foreground: false }) {
    const tool = registry.get(name);
    if (!tool)
        return null;
    /*
     * THE CAPABILITY DOOR, checked before the arguments are even parsed.
     *
     * A tool left out of the declarations can still be called: a model working from a stale turn, a
     * hand-made request, a name it remembers. So the omission above is courtesy and this is the
     * boundary — the same shape the worker's `searchWeb` uses for the server half.
     */
    const blocked = tool.unavailable?.();
    if (blocked)
        return { ok: false, message: blocked };
    let args;
    try {
        const parsed = argsJson.trim() ? JSON.parse(argsJson) : {};
        args = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    }
    catch (e) {
        // The most common real failure and the most fixable, so it names the tool and the fields rather
        // than reporting a parse error the model cannot locate.
        return {
            ok: false,
            message: `The arguments for "${name}" were not valid JSON (${msg(e)}). ` +
                `Send a single JSON object with these fields: ${Object.keys(tool.parameters.properties).join(', ') || '(none)'}.`,
        };
    }
    try {
        return await tool.run(args, ctx);
    }
    catch (e) {
        // The contract says `run` never throws. It threw. The model still gets a sentence it can act
        // on, and, crucially, an outcome at all, so the two-phase record resolves instead of dangling.
        return { ok: false, message: `"${name}" could not be run: ${msg(e)}.`, retryable: true };
    }
}
const msg = (e) => (e instanceof Error ? e.message : String(e));
//# sourceMappingURL=tools.js.map